import { ref, computed, readonly } from 'vue';
import {
    getStoredTokens,
    saveTokens,
    updateStoredTokens,
    clearStoredTokens,
    type Tokens,
    type UpdateTokens,
} from '@/shared/lib/auth';
import type { paths } from '@/shared/api/autogen/schema';

type AuthUserResponse = paths['/auth/user']['get']['responses']['200']['content']['application/json'];
type User = AuthUserResponse['user'];

const createAuthStore = () => {
    const tokens = ref<Tokens>();
    const user = ref<User | null>(null);

    const isAuthenticated = computed(() => !!tokens.value?.accessToken);

    const role = computed(() => {
        const r = user.value?.role;
        return {
            isAdmin: r === 'at-admin' || r === 'at-sc-admin',
            /** 最上位の管理者のみ（スタッフ管理など at-admin 限定機能の判定用） */
            isAtAdmin: r === 'at-admin',
            isHotel: r === 'hotel' || r === 'hotel-admin',
            isAirport: r === 'at-airport',
            isSC: r === 'at-sc',
        };
    });

    const getAccessToken = () => tokens.value?.accessToken ?? null;
    const getRefreshToken = () => tokens.value?.refreshToken ?? null;

    const setTokens = (newTokens: Tokens) => {
        tokens.value = { ...newTokens };
        saveTokens(newTokens);
    };

    const updateTokens = (newTokens: UpdateTokens) => {
        if (!tokens.value) return;
        tokens.value = { ...tokens.value, ...newTokens };
        updateStoredTokens(newTokens);
    };

    const clearAuth = () => {
        tokens.value = undefined;
        user.value = null;
        clearStoredTokens();
    };

    const setUser = (userData: User) => {
        user.value = userData;
    };

    const initializeAuth = () => {
        tokens.value = getStoredTokens();
    };

    return {
        user: readonly(user),
        isAuthenticated,
        role,
        getAccessToken,
        getRefreshToken,
        setTokens,
        updateTokens,
        clearAuth,
        setUser,
        initializeAuth,
    };
};

export type AuthStore = ReturnType<typeof createAuthStore>;

export const authStore = createAuthStore();
